/**
 * Connection status indicator.
 *
 * A SPA keeps rendering whatever it already has when the network drops, so
 * nothing tells the user anything is wrong until they navigate or trigger a
 * request — and then they get a generic failure. This surfaces the state
 * itself: a persistent pill while offline, and a brief confirmation when the
 * connection comes back so they know it is worth retrying.
 *
 * Deliberately does NOT block the page. The content already on screen stays
 * readable and useful offline; taking it away would be a downgrade.
 */

import { IconWifiOff, IconWifi } from "@tabler/icons-react";
import { FC, useEffect, useRef, useState } from "react";

import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { useStyles } from "./OfflineBanner.styles";

/** How long the "back online" confirmation stays up before fading out. */
const RESTORED_MS = 4000;

const OfflineBanner: FC = () => {
  const { classes, cx } = useStyles();
  const online = useOnlineStatus();
  const [showRestored, setShowRestored] = useState(false);

  // Tracks whether we have actually been offline this session. Without it, the
  // very first render would flash "Back online" at every user who simply
  // opened the app with a working connection.
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setShowRestored(false);
      return;
    }

    if (!wasOffline.current) return;

    wasOffline.current = false;
    setShowRestored(true);
    const id = setTimeout(() => setShowRestored(false), RESTORED_MS);
    return () => clearTimeout(id);
  }, [online]);

  if (online && !showRestored) return null;

  return (
    <div
      className={cx(classes.bar, online ? classes.online : classes.offline)}
      // `alert` while offline (it interrupts, because it changes what the app
      // can do); `status` for the recovery, which is merely informative.
      role={online ? "status" : "alert"}
      aria-live={online ? "polite" : "assertive"}
      data-testid="offline-banner"
    >
      {online ? (
        <IconWifi size={18} stroke={2} className={classes.icon} aria-hidden />
      ) : (
        <IconWifiOff size={18} stroke={2} className={classes.icon} aria-hidden />
      )}
      <span className={classes.text}>
        {online
          ? "Back online."
          : "No internet connection. You can keep reading this page, but nothing will save or load until you reconnect."}
      </span>
    </div>
  );
};

export default OfflineBanner;
